import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { format } from 'date-fns';
import { Plus, Edit2, Trash2, Calendar, FileText, X, Save, ChevronLeft, ChevronRight, Server, ArrowLeft, Eye } from 'lucide-react';

const PlatformForm = ({ platform, onSubmit, onCancel, isEdit = false, onPlatformChange }) => {
  const nameInputRef = useRef(null);

  useEffect(() => {
    // Focus the name input when the form first appears
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  const addDetailSection = () => {
    const newPlatform = {
      ...platform,
      details: [...platform.details, { title: '', subtitle: '', description: '' }],
    };
    onPlatformChange(newPlatform);
  };

  const updateDetail = (index, field, value) => {
    const newDetails = [...platform.details];
    newDetails[index] = { ...newDetails[index], [field]: value };
    const newPlatform = { ...platform, details: newDetails };
    onPlatformChange(newPlatform);
  };

  const removeDetailSection = (index) => {
    const newPlatform = {
      ...platform,
      details: platform.details.filter((_, i) => i !== index),
    };
    onPlatformChange(newPlatform);
  };

  const handleNameChange = (value) => {
    const newPlatform = { ...platform, name: value };
    onPlatformChange(newPlatform);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-4 sm:px-6 py-4">
        <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
          {isEdit ? <Edit2 size={18} className="sm:w-5 sm:h-5" /> : <Plus size={18} className="sm:w-5 sm:h-5" />}
          <span className="truncate">{isEdit ? 'Edit Platform' : 'Create New Platform'}</span>
        </h3>
      </div>
      
      <form onSubmit={onSubmit} className="p-4 sm:p-6 space-y-6">
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">Platform Name</label>
          <input
            type="text"
            ref={nameInputRef}
            value={platform.name}
            onChange={(e) => handleNameChange(e.target.value)}
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all text-sm sm:text-base"
            placeholder="Enter platform name"
            required
          />
        </div>

        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <label className="block text-sm font-semibold text-gray-700">Platform Details</label>
            <button
              type="button"
              onClick={addDetailSection}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm w-full sm:w-auto"
            >
              <Plus size={16} />
              Add Section
            </button>
          </div>

          {platform.details.map((detail, index) => (
            <div key={index} className="bg-gray-50 rounded-xl p-3 sm:p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm font-medium text-gray-600">Section {index + 1}</span>
                {platform.details.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeDetailSection(index)}
                    className="flex items-center gap-1 px-2 sm:px-3 py-1 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors text-xs sm:text-sm"
                  >
                    <X size={12} className="sm:w-3.5 sm:h-3.5" />
                    Remove
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    value={detail.title}
                    onChange={(e) => updateDetail(index, 'title', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all text-sm"
                    placeholder="Enter title"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">Subtitle</label>
                  <input
                    type="text"
                    value={detail.subtitle}
                    onChange={(e) => updateDetail(index, 'subtitle', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all text-sm"
                    placeholder="Enter subtitle"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-xs sm:text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={detail.description}
                  onChange={(e) => updateDetail(index, 'description', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all resize-none text-sm"
                  placeholder="Enter description"
                  rows={3}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pt-4">
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              type="submit"
              className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-slate-800 text-white rounded-xl hover:bg-slate-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base w-full sm:w-auto"
            >
              <Save size={16} />
              {isEdit ? 'Update Platform' : 'Create Platform'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors text-sm sm:text-base w-full sm:w-auto"
            >
              <X size={16} />
              Cancel
            </button>
          </div>

          <div className="hidden sm:flex items-center">
            <button
              type="button"
              onClick={addDetailSection}
              className="flex items-center gap-2 px-4 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm"
            >
              <Plus size={16} />
              Add Section
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

const PlatformDetail = ({ platform, onBack, onEdit, onDelete, isLoading }) => {
  return (
    <div className="space-y-6">
      {/* Back button and header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
        >
          <ArrowLeft size={16} />
          Back to Platforms
        </button>
      </div>

      {/* Platform details */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-4 sm:px-6 py-4">
          <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
            <Eye size={18} className="sm:w-5 sm:h-5" />
            <span className="truncate">{platform.name}</span>
          </h2>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          {/* Platform info */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Calendar size={16} />
              <span>Created: {format(new Date(platform.createdAt), 'MMM dd, yyyy')}</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={() => onEdit(platform)}
                disabled={isLoading}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm w-full sm:w-auto disabled:opacity-50"
              >
                <Edit2 size={16} />
                Edit Platform
              </button>
              <button
                onClick={() => onDelete(platform.id)}
                disabled={isLoading}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors text-sm w-full sm:w-auto disabled:opacity-50"
              >
                <Trash2 size={16} />
                Delete Platform
              </button>
            </div>
          </div>

          {/* Platform details */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Platform Details</h3>
            {(platform.details || []).length === 0 ? (
              <div className="text-center py-8">
                <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No details added yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {platform.details.map((detail, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-medium text-gray-500 bg-gray-200 px-2 py-1 rounded">
                        Section {index + 1}
                      </span>
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2 text-base">{detail.title || 'No Title'}</h4>
                    {detail.subtitle && (
                      <p className="font-medium text-gray-600 mb-3 text-sm">{detail.subtitle}</p>
                    )}
                    {detail.description && (
                      <p className="text-gray-500 text-sm leading-relaxed">{detail.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Platforms() {
  const [platforms, setPlatforms] = useState({
    platforms: [],
    total: 0,
    page: 1,
    limit: 10,
  });
  const [newPlatform, setNewPlatform] = useState({ name: '', details: [{ title: '', subtitle: '', description: '' }] });
  const [editPlatform, setEditPlatform] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('adminToken');

  const fetchPlatforms = async (page = 1) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await axios.get(
        `https://awriter-backend-si7g.onrender.com/api/platforms?page=${page}&limit=10`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPlatforms({
        platforms: response.data.platforms || [],
        total: response.data.total || 0,
        page: response.data.page || 1,
        limit: response.data.limit || 10,
      });
    } catch (error) {
      console.error('Error fetching platforms:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to fetch platforms');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePlatform = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      await axios.post(
        'https://awriter-backend-si7g.onrender.com/api/platforms',
        newPlatform,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewPlatform({ name: '', details: [{ title: '', subtitle: '', description: '' }] });
      setShowCreateForm(false);
      await fetchPlatforms(platforms.page);
    } catch (error) {
      console.error('Error creating platform:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to create platform');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePlatform = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      await axios.put(
        `https://awriter-backend-si7g.onrender.com/api/platforms/${editPlatform.id}`,
        { name: editPlatform.name, details: editPlatform.details },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditPlatform(null);
      setSelectedPlatform(null); // Go back to list after editing
      await fetchPlatforms(platforms.page);
    } catch (error) {
      console.error('Error updating platform:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to update platform');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePlatform = async (id) => {
    if (!window.confirm('Are you sure you want to delete this platform? This action cannot be undone.')) return;
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      await axios.delete(
        `https://awriter-backend-si7g.onrender.com/api/platforms/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedPlatform(null); // Go back to list after deleting
      await fetchPlatforms(platforms.page);
    } catch (error) {
      console.error('Error deleting platform:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to delete platform');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (platform) => {
    setEditPlatform({ id: platform.id, name: platform.name, details: platform.details || [{ title: '', subtitle: '', description: '' }] });
  };

  const handleCancelEdit = () => {
    setEditPlatform(null);
  };

  const handlePlatformClick = (platform) => {
    setSelectedPlatform(platform);
  };

  const handleBackToList = () => {
    setSelectedPlatform(null);
    setEditPlatform(null);
  };

  useEffect(() => {
    fetchPlatforms();
    window.scrollTo(0, 0);
  }, []);

  const handlePageChange = (newPage) => {
    fetchPlatforms(newPage);
  };

  // Show platform detail view
  if (selectedPlatform && !editPlatform) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:py-8 space-y-6 sm:space-y-8">
        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
            {errorMessage}
          </div>
        )}
        
        <PlatformDetail
          platform={selectedPlatform}
          onBack={handleBackToList}
          onEdit={handleEditClick}
          onDelete={handleDeletePlatform}
          isLoading={isLoading}
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:py-8 space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">Platform Management</h1>
        <p className="text-gray-600 text-base sm:text-lg">Manage platforms for Tips & Suggestions</p>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {errorMessage}
        </div>
      )}

      {/* Add Platform Button */}
      {!showCreateForm && !editPlatform && (
        <div className="text-center">
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-slate-800 text-white rounded-xl hover:bg-slate-900 transition-colors hover:transform hover:scale-105 text-sm sm:text-base w-full sm:w-auto max-w-xs sm:max-w-none mx-auto"
          >
            <Plus size={18} className="sm:w-5 sm:h-5" />
            Add Platform
          </button>
        </div>
      )}

      {/* Create Platform Form */}
      {showCreateForm && (
        <PlatformForm
          platform={newPlatform}
          onSubmit={handleCreatePlatform}
          onCancel={() => {
            setShowCreateForm(false);
            setNewPlatform({ name: '', details: [{ title: '', subtitle: '', description: '' }] });
          }}
          onPlatformChange={setNewPlatform}
        />
      )}

      {/* Edit Platform Form */}
      {editPlatform && (
        <PlatformForm
          platform={editPlatform}
          onSubmit={handleUpdatePlatform}
          onCancel={handleCancelEdit}
          onPlatformChange={setEditPlatform}
          isEdit={true}
        />
      )}

      {/* Platforms List */}
      {!showCreateForm && !editPlatform && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-4 sm:px-6 py-4">
            <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
              <Server size={18} className="sm:w-5 sm:h-5" />
              <span className="truncate">All Platforms ({platforms.total})</span>
            </h2>
          </div>

          <div className="p-4 sm:p-6">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : platforms.platforms.length === 0 ? (
              <div className="text-center py-12">
                <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 text-lg mb-4">No platforms found</p>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-800 text-white rounded-xl hover:bg-slate-900 transition-colors text-sm"
                >
                  <Plus size={16} />
                  Create Your First Platform
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {platforms.platforms.map(platform => (
                  <div
                    key={platform.id}
                    onClick={() => handlePlatformClick(platform)}
                    className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 hover:shadow-md transition-all cursor-pointer border border-gray-200 hover:border-gray-300"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">{platform.name}</h3>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                          <Calendar size={14} />
                          <span>Created: {format(new Date(platform.createdAt), 'MMM dd, yyyy')}</span>
                          <span className="text-gray-300">•</span>
                          <span>{(platform.details || []).length} section{(platform.details || []).length !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Eye size={16} />
                        <span>Click to view</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {platforms.total > 0 && platforms.platforms.length > 0 && (
            <div className="bg-gray-50 px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div className="text-xs sm:text-sm text-gray-700 text-center sm:text-left">
                Showing {(platforms.page - 1) * platforms.limit + 1} to{' '}
                {Math.min(platforms.page * platforms.limit, platforms.total)} of{' '}
                {platforms.total} platforms
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={() => handlePageChange(platforms.page - 1)}
                  disabled={platforms.page === 1}
                  className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm flex-1 sm:flex-none"
                >
                  <ChevronLeft size={16} />
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </button>
                <button
                  onClick={() => handlePageChange(platforms.page + 1)}
                  disabled={platforms.page * platforms.limit >= platforms.total}
                  className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm flex-1 sm:flex-none"
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}